import { atom } from 'jotai'
import { atomFamily, atomWithStorage } from 'jotai/utils'
import { CUE_TYPE_CUE } from '../constants/cue-types'
import { runnerAtom } from './runner.store'
import type {
  Rundown,
  RundownCell,
  RundownColumn,
  RundownCue,
  Runner,
  RundownCueOrderItem,
} from '@rundown-studio/types'
import { CueStartMode, CueType } from '@rundown-studio/types'
import _findIndex from 'lodash/findIndex'
import _keyBy from 'lodash/keyBy'
import { parseCueApiResponse } from '../utils/parseSnapshotWrappers'
import { parseRundownDate } from '../utils/parse-rundown-date'
import type { IndexedCue } from '@/features/cues-list'
import _groupBy from 'lodash/groupBy'
import { getIndexWithPrefix } from '@rundown-studio/utils'
import * as OOOService from '../services/OOOService'
import { initRundownPageStore } from '@/features/rundown/rundown-page.store'

// -----------------------------------------------
// Rundown
// -----------------------------------------------
export const rundownAtom = atom<Rundown | null, [Rundown | null], void>(
  null,
  (_, set, updatedRundown: Rundown | null) => {
    if (!updatedRundown) {
      set(rundownAtom, null)
      return
    }

    const parsedRundown = {
      ...updatedRundown,
      startTime: parseRundownDate(updatedRundown.startTime) ?? updatedRundown.startTime,
      endTime: parseRundownDate(updatedRundown.endTime) ?? null,
    }

    initRundownPageStore(parsedRundown)
    set(rundownAtom, parsedRundown)
  },
)

export const clearRundownAtom = atom(null, (_, set) => {
  set(rundownAtom, null)
})

export const rundownSettingsAtom = atom(
  (get) => get(rundownAtom)?.settings || {},
)

// -----------------------------------------------
// Rundown Cells
// -----------------------------------------------
const _cellsInitAtom = atom(false)
export const cellsInitAtom = atom(
  (get) => get(_cellsInitAtom),
  (_, set) => {
    set(_cellsInitAtom, true)
  },
)

/**
 * Coerce a cell's `updatedAt` to ms epoch for OOOService.
 * Snapshots arrive as Dates; PATCH responses arrive as ISO strings after JSON
 * serialization. Returns undefined when absent — service treats as untimed.
 */
function cellUpdatedAtMs(cell: RundownCell): number | undefined {
  const value = cell.updatedAt as Date | string | null | undefined
  if (!value) return undefined
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime()
  return Number.isNaN(ms) ? undefined : ms
}

const _cellsAtom = atom<Record<string, RundownCell>>({})
export const cellsAtom = atom(
  (get) => Object.values(get(_cellsAtom)),
  (get, set, updatedCells: RundownCell[]) => {
    // Snapshot is authoritative for the id-set: cells absent from the snapshot
    // are removed. For cells present in both, OOOService gates the overwrite —
    // an older snapshot won't stomp a fresher PATCH response.
    const existing = get(_cellsAtom)
    const next: Record<string, RundownCell> = {}
    for (const incoming of updatedCells) {
      const prior = existing[incoming.id]
      if (prior && !OOOService.accept(`cell:${incoming.id}`, cellUpdatedAtMs(incoming))) {
        next[incoming.id] = prior
      } else {
        next[incoming.id] = incoming
      }
    }
    set(_cellsAtom, next)
  },
)

export const setCellsAtom = atom(
  null, // write-only atom
  (get, set, cellsArray: RundownCell[]) => {
    const cells = get(_cellsAtom)
    cellsArray.map((cell) => (cells[cell.id] = cell))
    set(_cellsAtom, cells)
  },
)

export const setCellByIdAtom = atom(
  null, // write-only atom
  (get, set, cell: RundownCell) => {
    if (!OOOService.accept(`cell:${cell.id}`, cellUpdatedAtMs(cell))) return
    const cells = get(_cellsAtom)
    cells[cell.id] = cell
    set(_cellsAtom, cells)
  },
)

// -----------------------------------------------
// Rundown Columns
// -----------------------------------------------
const _columnsInitAtom = atom(false)
export const columnsInitAtom = atom(
  (get) => get(_columnsInitAtom),
  (_, set) => {
    set(_columnsInitAtom, true)
  },
)

const _columnsAtom = atom<Record<string, RundownColumn>>({})
// Getter: Returns an array of columns sorted by rundown.columns DB order (no user layout applied).
// Setter: Accepts an array of columns (unsorted).
// Consumers that need the user's local column layout (reordering, width overrides,
// hidden columns) call `useColumnLayout(rundownId)` instead — it layers the
// per-rundown localStorage snapshot on top of this base order at read time.
export const columnsAtom = atom(
  (get) => {
    const rundown = get(rundownAtom)
    return Object.values(get(_columnsAtom))
      .filter((column) => rundown?.columns?.includes(column.id))
      .sort((a, b) => {
        const indexA = rundown?.columns?.indexOf(a.id) ?? 0
        const indexB = rundown?.columns?.indexOf(b.id) ?? 0
        return indexA - indexB
      })
  },
  (_, set, updatedColumns: RundownColumn[]) => {
    set(_columnsAtom, _keyBy(updatedColumns, 'id'))
  },
)

// Getter: Returns a getter for a columns by id
export const getColumnById = atom<(id: string) => RundownColumn | undefined>(
  (get) => (id: string) => get(_columnsAtom)[id],
)

// Returns the count of columns visible in the rundown
export const columnCountAtom = atom<number>((get) => {
  const columns = get(columnsAtom)
  return columns.length
})

// -----------------------------------------------
// Rundown Cues
// -----------------------------------------------
const _cuesInitAtom = atom<boolean>(false)
export const cuesInitAtom = atom<boolean, [boolean], void>(
  (get) => get(_cuesInitAtom),
  (_, set) => {
    set(_cuesInitAtom, true)
  },
)

const _cuesAtom = atom<Record<string, RundownCue>>({})
// Getter: Returns an array of cueIds (sorted)
export const flatCueIdsAtom = atom<string[] | undefined>((get) => {
  const rundown = get(rundownAtom)
  return rundown?.cues
    .map((cue) =>
      cue?.children?.length
        ? [cue.id, ...cue.children.map((child) => child.id)]
        : [cue.id],
    )
    .flat()
})

// Getter: Returns an array of cues (sorted)
export const filteredAndSortedCuesAtom = atom<RundownCue[]>((get) => {
  const flatCueIds = get(flatCueIdsAtom)
  const cues = get(_cuesAtom)
  return Object.values(cues)
    .filter((cue) => flatCueIds?.includes(cue.id))
    .sort((a, b) => {
      const indexA = flatCueIds?.indexOf(a.id) ?? 0
      const indexB = flatCueIds?.indexOf(b.id) ?? 0
      return indexA - indexB
    })
})

export const firstCueIdAtom = atom<string | null>((get) => {
  const sortedCues = get(filteredAndSortedCuesAtom)
  // Iterate from the start to find the first CUE_TYPE_CUE
  for (const cue of sortedCues) {
    if (cue.type === CUE_TYPE_CUE) return cue.id
  }
  return null
})

export const lastCueIdAtom = atom<string | null>((get) => {
  const sortedCues = get(filteredAndSortedCuesAtom)
  // Iterate from the end to find the last CUE_TYPE_CUE
  for (let i = sortedCues.length - 1; i >= 0; i--) {
    if (sortedCues[i].type === CUE_TYPE_CUE) return sortedCues[i].id
  }
  return null
})

export const getPreviousCueAtom = atom<(cueId: string) => RundownCue | null>(
  (get) => {
    const sortedCues = get(filteredAndSortedCuesAtom)
    return (cueId: string) => {
      // Find received id index
      const idx = sortedCues.findIndex((c) => c.id === cueId)

      // If not found or first
      if (idx === -1 || idx === 0) {
        return null
      }

      // Iterate backwards to find previous with type CUE
      for (let i = idx - 1; i >= 0; i--) {
        if (sortedCues[i].type === CUE_TYPE_CUE) {
          return sortedCues[i]
        }
      }

      // If not found, return null
      return null
    }
  },
)

export const getNextCueAtom = atom<(cueId: string) => RundownCue | null>(
  (get) => {
    const sortedCues = get(filteredAndSortedCuesAtom)
    return (cueId: string) => {
      // Find received id index
      const idx = sortedCues.findIndex((c) => c.id === cueId)

      // If not found or last
      if (idx === -1 || idx === sortedCues.length - 1) {
        return null
      }

      // Iterate forwards to find next with type CUE
      for (let i = idx + 1; i < sortedCues.length; i++) {
        if (sortedCues[i].type === CUE_TYPE_CUE) {
          return sortedCues[i]
        }
      }

      // If not found, return null
      return null
    }
  },
)

// Getter: Returns a map of cues (unsorted)
// Setter: Accepts an array (!) of cues (unsorted)
export const cuesAtom = atom<Record<string, RundownCue>, [RundownCue[]], void>(
  (get) => {
    const filteredAndSortedCues = get(filteredAndSortedCuesAtom)
    return _keyBy(filteredAndSortedCues, 'id')
  },
  (_, set, updatedCues) => {
    set(_cuesAtom, _keyBy(updatedCues.map(parseCueApiResponse), 'id'))
  },
)

// rundown.cues:
// [
//   { id: string },
//   { id: string },
//   { id: string },
//   {
//     id: string,
//     children: [
//       { id: string },
//       { id: string },
//       { id: string },
//     ]
//   }
// ]

export const setCueAtom = atom(
  null, // write-only atom
  (get, set, cue: RundownCue) => {
    const cues = get(_cuesAtom)
    cues[cue.id] = parseCueApiResponse(cue)
    set(_cuesAtom, cues)
  },
)

export const setCuesAtom = atom(
  null, // write-only atom
  (get, set, cuesArray: RundownCue[]) => {
    const cues = get(_cuesAtom)
    cuesArray.map((cue) => (cues[cue.id] = parseCueApiResponse(cue)))
    set(_cuesAtom, cues)
  },
)

export const setCueOrderAtom = atom(
  null, // write-only atom
  (
    get,
    set,
    cueIds: Array<{ id: string; children?: Array<{ id: string }> }>,
  ) => {
    const rundown = get(rundownAtom)
    set(rundownAtom, { ...(rundown as Rundown), cues: cueIds })
  },
)

// Toggled groups atom
export const collapsedGroupsAtom = atom<string[]>([])
export const toggleCollapsedGroupsAtom = atom(
  null, // write-only atom
  (get, set, toggleStatus: boolean) => {
    const filteredAndSortedCues = get(filteredAndSortedCuesAtom)
    if (toggleStatus) {
      set(
        collapsedGroupsAtom,
        filteredAndSortedCues
          .filter((c) => c.type === 'group')
          .map((c) => c.id),
      )
    } else {
      set(collapsedGroupsAtom, [])
    }
  },
)
export const collapseGroupAtom = atom(
  null,
  (get, set, { cueId, force = false }: { cueId: string; force?: boolean }) => {
    const collapsedGroups = get(collapsedGroupsAtom)

    if (!collapsedGroups.includes(cueId) || force) {
      set(collapsedGroupsAtom, [...collapsedGroups, cueId])
    } else {
      set(
        collapsedGroupsAtom,
        collapsedGroups.filter((id) => id !== cueId),
      )
    }
  },
)

// Per-cue derived atom keyed by cueId. Use this from any component that only
// needs to know whether one specific group is collapsed (the chevron icon,
// the tree-line connector). Reading `collapsedGroupsAtom` directly causes
// every subscriber to re-render whenever any group toggles — on large
// rundowns that meant N CueItems re-rendering per click and locked up Safari.
// With this family, each subscriber resolves to a boolean and Jotai skips
// the re-render when the boolean hasn't flipped.
export const isGroupCollapsedAtomFamily = atomFamily((cueId: string) =>
  atom((get) => get(collapsedGroupsAtom).includes(cueId)),
)

// `indexedCuesAtom` with children of collapsed groups filtered out. The cues
// list consumes this directly so collapsed children are actually unmounted —
// previously they stayed in the DOM with `display: none`, which still forced
// Safari into an expensive style/layout pass over every hidden TipTap editor
// on each toggle.
export const visibleIndexedCuesAtom = atom<IndexedCue[]>((get) => {
  const cues = get(indexedCuesAtom)
  const collapsedGroups = get(collapsedGroupsAtom)
  if (collapsedGroups.length === 0) return cues
  const collapsed = new Set(collapsedGroups)
  return cues.filter(({ parentId }) => !parentId || !collapsed.has(parentId))
})

// AutoScroll functionality
interface AutoScrollState {
  [rundownId: string]: boolean
}

const internalAutoScrollAtom = atomWithStorage<AutoScrollState>(
  'autoScroll',
  {},
)

export const autoScrollAtom = atom(
  (get) => {
    return get(internalAutoScrollAtom)
  },
  (get, set, rundownId: string, newValue: boolean) => {
    const current = get(internalAutoScrollAtom)

    const updated = { ...current, [rundownId]: newValue }

    set(internalAutoScrollAtom, updated)
  },
)

// Selected cues / Batch operations
export const selectedCueIdsAtom = atom<string[]>([])

export const toggleSelectedCuesAtom = atom(
  null, // write-only atom
  (get, set, id: string) => {
    const selectedIds = get(selectedCueIdsAtom)
    if (selectedIds.includes(id)) {
      set(
        selectedCueIdsAtom,
        selectedIds.filter((selectedId) => selectedId !== id),
      )
    } else {
      set(selectedCueIdsAtom, [...selectedIds, id])
    }
  },
)

export const isCueSelectedAtom = atom<(cueId?: string | null) => boolean>(
  (get) => (cueId?: string | null) => {
    const selectedIds = get(selectedCueIdsAtom)

    if (!cueId) return false

    return selectedIds.includes(cueId)
  },
)

// Get next hard start cue
export const nextHardStartCueIdAtom = atom<string | null>((get) => {
  const allCueIds = get(flatCueIdsAtom)
  const cues = get(cuesAtom)
  const runner = get(runnerAtom) as Runner | null

  if (!runner || !allCueIds) return null

  // Find the current running cue index
  const currentCueIndex = runner?.timesnap.cueId
    ? allCueIds.findIndex((cueId) => cueId === runner?.timesnap.cueId)
    : -1

  // Find the next hard start cue (startMode === "fixed")
  const nextHardStartCueId = allCueIds.find((cueId, index) => {
    if (index <= currentCueIndex) return false
    const cueData = cues[cueId]
    return (
      cueData?.type === CueType.CUE && cueData?.startMode === CueStartMode.FIXED
    )
  })

  // Set the next hard start cue ID
  return nextHardStartCueId || null
})

export const isCueInSoftSchedulePathAtom = atom<
  (cueId: string | null) => boolean
>((get) => {
  return (cueId: string | null) => {
    const flatCueIds = get(flatCueIdsAtom)
    const cues = get(cuesAtom)
    const firstCueId = get(firstCueIdAtom)

    if (!cueId || !flatCueIds || !cues) return false

    // Check if current cue exists
    const currentCue = cues[cueId]
    if (!currentCue) return false

    let targetCue: RundownCue | null = null

    // If current cue is a CUE type, it's the target
    if (currentCue.type === CueType.CUE) {
      targetCue = currentCue
    } else {
      // For non-CUE types, find the next CUE type
      const currentIndex = flatCueIds.indexOf(cueId)
      if (currentIndex === -1) return false

      const nextCueIndex = _findIndex(
        flatCueIds,
        (id, index) => index > currentIndex && cues[id]?.type === CueType.CUE,
      )

      // If no next cue found, return false
      if (nextCueIndex === -1) return false

      // Get the next cue
      const nextCueId = flatCueIds[nextCueIndex]
      targetCue = cues[nextCueId]
    }

    // Special case: First cue is always considered FIXED
    if (targetCue.id === firstCueId) {
      return false
    }

    // Check whether the target cue is soft-scheduled
    return (
      targetCue.startMode === CueStartMode.FLEXIBLE &&
      targetCue.scheduled === true
    )
  }
})

// Returns the count of pure cues (type === 'cue') in the rundown
// Pure cues are the only ones that have cells, hence they are what matters for virtualization calculations
export const pureCueCountAtom = atom<number>((get) => {
  const cues = get(filteredAndSortedCuesAtom)
  return cues.filter((cue) => cue.type === CUE_TYPE_CUE).length
})

/**
 * An atom representing a derived state of cues paired with their metadata.
 * It represents cue metadata, including display indices and sorted by the order in rundown.
 *
 * This atom computes a list of cues with additional metadata such as display indices and hierarchy
 * based on the state of `rundownAtom` and `_cuesAtom`. If the rundown or cues are not available,
 * the result is an empty array.
 *
 * The `cuesMetaAtom` processes:
 * - Flattening of cues, including child cues if they exist.
 * - Assigning an incremental display index for each cue and child cue.
 * - Tagging metadata such as `displayIndex`, `parentId`, and `lastSibling` for hierarchical navigation.
 *
 * Cues are categorized and handled differently based on their type (e.g., HEADING, GROUP).
 * - For a cue of type HEADING, it is skipped for increasing the display index.
 * - For a cue of type GROUP that includes children, both the group and its children are processed and included in the result.
 */
export const indexedCuesAtom = atom<IndexedCue[]>((get) => {
  const rundown = get(rundownAtom)
  const cues = get(_cuesAtom)

  if (!rundown?.cues) return []
  if (!Object.keys(cues).length) return []

  const startFrom = rundown.settings.cueIndexStartFrom
  const prefix = rundown.settings.cueIndexPrefix
  const padding = rundown.settings.cueIndexPadding

  let displayIndex = startFrom - 1
  const result: IndexedCue[] = []
  let flatIndex = 0

  const rundownCues = rundown.cues
  const rundownCuesLength = rundownCues.length

  for (let i = 0; i < rundownCuesLength; i++) {
    const cue = rundownCues[i]
    const cueData = cues[cue.id]

    if (!cueData) continue

    const isHeading = cueData.type === CueType.HEADING
    const isGroup =
      cueData.type === CueType.GROUP && Array.isArray(cue.children)

    if (!isHeading) displayIndex += 1

    const baseItem: IndexedCue = {
      id: cue.id,
      index: flatIndex,
      displayIndex: getIndexWithPrefix(displayIndex, prefix, padding),
      lastSibling: false,
    }

    if (!isGroup) {
      result.push(baseItem)
      flatIndex += 1
      continue
    }

    baseItem.children = cue.children
    result.push(baseItem)
    flatIndex += 1

    let childDisplayIndex = 0
    const children = cue.children as RundownCueOrderItem[]
    const childrenLength = children.length

    for (let j = 0; j < childrenLength; j++) {
      const child = children[j]
      const childCueData = cues[child.id]

      if (!childCueData) continue

      const childIsHeading = childCueData.type === CueType.HEADING

      if (!childIsHeading) childDisplayIndex += 1

      result.push({
        id: child.id,
        index: flatIndex,
        displayIndex: `${getIndexWithPrefix(displayIndex, prefix, padding)}.${childDisplayIndex}`,
        parentId: cue.id,
        lastSibling: j + 1 === childrenLength,
      })
      flatIndex += 1
    }
  }

  return result
})

export const indexedCuesMapAtom = atom<Record<string, IndexedCue>>((get) => {
  return _keyBy(get(indexedCuesAtom), 'id')
})

/**
 * Indexed cues with skipDuringShow cues (and their group children) removed.
 * Used by output views (cue list, prompter) where skipped cues should be
 * completely hidden — as opposed to the editor which shows all cues with
 * visual skip indicators.
 */
export const indexedCuesWithoutSkippedAtom = atom<IndexedCue[]>((get) => {
  const cues = get(_cuesAtom)
  return get(indexedCuesAtom).filter(({ id, parentId }) => {
    if (cues[id]?.settings?.skipDuringShow) return false
    if (parentId && cues[parentId]?.settings?.skipDuringShow) return false
    return true
  })
})

/** Flat list of cue IDs with skipped cues removed. See indexedCuesWithoutSkippedAtom. */
export const flatCueIdsWithoutSkippedAtom = atom<string[]>((get) =>
  get(indexedCuesWithoutSkippedAtom).map(({ id }) => id),
)

export const getCueCellsAtom = atom((get) => {
  const cells = get(_cellsAtom)
  const cellMap = _groupBy(cells, (cell) => cell.cueId)

  return (cueId: string) => cellMap[cueId]
})

export const getCueAtom = atom((get) => {
  const cues = get(_cuesAtom)

  return (cueId: string) => cues[cueId]
})

export const getIndexedCueAtom = atom((get) => {
  const cuesMeta = get(indexedCuesMapAtom)

  return (cueId: string) => cuesMeta[cueId]
})
